import { Box, Paper, Typography } from '@mui/material';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { getFuncao } from '../../../events/components/EventList/EventMap';
import { Question } from '../../types/questionnaire.types';
import { isQuestionAnswered } from '../../utils/questionValidation';
import { ProgressBar } from '../shared/ProgressBar';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getAnswerLetter = (
  question: Question,
  responseValue: string | undefined
): string => {
  if (!responseValue) return '–';

  if (question.type === 'multiple_choice' || question.type === 'true_false') {
    const idx =
      question.answers?.findIndex((a) => a.id === responseValue) ?? -1;
    return idx >= 0 ? String.fromCharCode(65 + idx) : '–';
  }

  if (question.type === 'essay') {
    return responseValue && responseValue !== '-' ? '✓' : '–';
  }

  return responseValue ? '✓' : '–';
};

/** Formata um Date como HH:MM:SS no fuso de Brasília */
// const formatBrasilia = (date: Date): string =>
//   date.toLocaleTimeString('pt-BR', {
//     timeZone: 'America/Sao_Paulo',
//     hour: '2-digit',
//     minute: '2-digit',
//     second: '2-digit',
//     hour12: false,
//   });

// ---------------------------------------------------------------------------
// HoraBrasilia
// ---------------------------------------------------------------------------

// const HoraBrasilia: React.FC = () => {
//   const [hora, setHora] = React.useState<string>('--:--:--');
//   const [status, setStatus] = React.useState<'loading' | 'synced' | 'local'>(
//     'loading'
//   );

//   React.useEffect(() => {
//     let interval: ReturnType<typeof setInterval>;
//     const controller = new AbortController();
//     const timeoutId = setTimeout(() => controller.abort(), 5000);

//     fetch(
//       'https://timeapi.io/api/time/current/zone?timeZone=America/Sao_Paulo',
//       { signal: controller.signal }
//     )
//       .then((res) => res.json())
//       .then((data) => {
//         clearTimeout(timeoutId);
//         const serverDate = new Date(data.dateTime);
//         if (Number.isNaN(serverDate.getTime()))
//           throw new Error('Data inválida');

//         setHora(formatBrasilia(serverDate));
//         setStatus('synced');

//         interval = setInterval(() => {
//           serverDate.setSeconds(serverDate.getSeconds() + 1);
//           setHora(formatBrasilia(serverDate));
//         }, 1000);
//       })
//       .catch(() => {
//         clearTimeout(timeoutId);
//         setStatus('local');
//         setHora(formatBrasilia(new Date()));
//         interval = setInterval(() => {
//           setHora(formatBrasilia(new Date()));
//         }, 1000);
//       });

//     return () => {
//       clearTimeout(timeoutId);
//       clearInterval(interval);
//       controller.abort();
//     };
//   }, []);

//   const tooltipText =
//     status === 'synced'
//       ? 'Horário oficial de Brasília (sincronizado)'
//       : status === 'local'
//         ? 'Horário estimado pelo relógio do dispositivo'
//         : 'Obtendo horário…';

//   const icon = status === 'local' ? '⚠' : '🕐';

//   return (
//     <Tooltip title={tooltipText} placement="top">
//       <Chip
//         label={`${icon}  HORÁRIO DE BRASÍLIA: ${hora}`}
//         size="small"
//         sx={{
//           fontVariantNumeric: 'tabular-nums',
//           fontWeight: 600,
//           mt: 0.5,
//           fontSize: '0.8rem',
//           letterSpacing: '0.4px',
//           color:
//             status === 'synced'
//               ? 'primary.dark'
//               : status === 'local'
//                 ? 'warning.dark'
//                 : 'text.disabled',
//           backgroundColor:
//             status === 'synced'
//               ? 'rgba(25, 118, 210, 0.10)'
//               : status === 'local'
//                 ? 'rgba(237, 108, 2, 0.08)'
//                 : 'rgba(0,0,0,0.04)',
//           border: '1px solid',
//           borderColor:
//             status === 'synced'
//               ? 'rgba(25, 118, 210, 0.25)'
//               : status === 'local'
//                 ? 'rgba(237, 108, 2, 0.25)'
//                 : 'rgba(0,0,0,0.08)',
//           cursor: 'default',
//           userSelect: 'none',
//           px: 0.5,
//         }}
//       />
//     </Tooltip>
//   );
// };

// ---------------------------------------------------------------------------
// MinhasRespostas
// ---------------------------------------------------------------------------

interface MinhasRespostasProps {
  questions: Question[];
  responses: Record<string, string>;
  currentQuestion?: number;
  onStepClick?: (index: number) => void;
}

export const MinhasRespostas: React.FC<MinhasRespostasProps> = ({
  questions,
  responses,
  currentQuestion,
  onStepClick,
}) => {
  if (!questions || questions.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 1.5,
          fontWeight: 600,
          color: 'primary.dark',
          fontSize: '11pt',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          textAlign: 'center',
        }}
      >
        My Answers
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '4px',
        }}
      >
        {questions.map((q, idx) => {
          const letter = getAnswerLetter(q, responses[q.id]);
          const isAnswered = letter !== '–';
          const isCurrent = idx === currentQuestion;
          return (
            <Box
              key={q.id}
              onClick={() => onStepClick?.(idx)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: onStepClick ? 'pointer' : 'default',
                transform: isCurrent ? 'scale(1.15)' : 'scale(1)',
                transition: 'transform 0.2s ease',
                '&:hover': onStepClick
                  ? { transform: 'scale(1.2)' }
                  : undefined,
              }}
            >
              {/* Número */}
              <Box
                sx={{
                  width: 24,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px 4px 0 0',
                  backgroundColor: isCurrent ? '#EEEDFE' : 'rgba(0,0,0,0.04)',
                  border: 'none',
                  borderBottom: isCurrent ? '2px solid #7F77DD' : undefined,
                  borderLeft: isCurrent ? '2px solid #7F77DD' : undefined,
                  borderRight: isCurrent ? '2px solid #7F77DD' : undefined,
                  borderTop: isCurrent ? '2px solid #7F77DD' : undefined,
                  boxShadow: 'none',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '9pt',
                    fontWeight: isCurrent ? 700 : 500,
                    color: isCurrent ? '#3C3489' : '#888780',
                    lineHeight: 1,
                  }}
                >
                  {idx + 1}
                </Typography>
              </Box>

              {/* Letra */}
              <Box
                sx={{
                  width: 24,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0 0 4px 4px',
                  backgroundColor: isAnswered
                    ? isCurrent
                      ? '#CECBF6'
                      : '#FAC775'
                    : 'rgba(0,0,0,0.04)',
                  borderColor: isAnswered
                    ? isCurrent
                      ? '#7F77DD'
                      : '#EF9F27'
                    : 'rgba(0,0,0,0.08)',
                  transition: 'all 0.2s ease',
                  borderLeft: isCurrent
                    ? '2px solid #7F77DD'
                    : '1px solid rgba(0,0,0,0.08)',
                  borderRight: isCurrent
                    ? '2px solid #7F77DD'
                    : '1px solid rgba(0,0,0,0.08)',
                  borderBottom: isCurrent
                    ? '2px solid #7F77DD'
                    : '1px solid rgba(0,0,0,0.08)',
                  borderTop: 'none',
                  boxShadow: 'none',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '10.5pt',
                    fontWeight: isAnswered ? 700 : 400,
                    color: isAnswered
                      ? isCurrent
                        ? '#26215C'
                        : '#412402'
                      : 'text.disabled',
                    // lineHeight: 1,
                  }}
                >
                  {letter}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// DadosServidorHeader
// ---------------------------------------------------------------------------

interface DadosServidorHeaderProps {
  questions: Question[];
  responses: Record<string, string>;
  currentQuestion?: number;
  onStepClick?: (index: number) => void;
}

export const DadosServidorHeader: React.FC<DadosServidorHeaderProps> = ({
  questions,
  responses,
  currentQuestion = 0,
  onStepClick,
}) => {
  const answeredCount = React.useMemo(
    () =>
      questions.filter((q) => isQuestionAnswered(q, responses[q.id])).length,
    [questions, responses]
  );
  const totalQuestions = questions.length;
  const progressPercentage =
    totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
  const location = useLocation();

  const evtIdMatch = location.pathname.match(/evtid=([^/]+)/i);
  const evtId = evtIdMatch ? evtIdMatch[1] : '';

  const nome = localStorage.getItem('name') || '';
  const nomeDaProva = getFuncao(evtId);

  if (!evtId) return null;

  return (
    <Box sx={{ mt: 3, px: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 1400,
          width: '100%',
          mx: 'auto',
          p: { xs: 2.5, md: 3 },
          borderRadius: 2,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid',
          borderColor: 'rgba(25, 118, 210, 0.3)',
          position: 'relative',
          overflow: 'visible',
          boxSizing: 'border-box',
          paddingLeft: '20px !important',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            backgroundColor: 'primary.main',
          },
        }}
      >
        {/* Texto de boas-vindas */}
        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            fontSize: { xs: '0.9rem', md: '1rem' },
            lineHeight: 1.8,
            textAlign: 'center',
            '& .destaque': {
              fontWeight: 500,
              color: 'primary.dark',
            },
          }}
        >
          👋 Hi, <span className="destaque">{nome}</span>! Ready to do your
          best? ⭐ You are taking the{' '}
          <span className="destaque">{nomeDaProva}</span>. Good luck! 🍀
        </Typography>
        {/* Horário de Brasília — chip centralizado entre o texto e as respostas */}
        {/* <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <HoraBrasilia />
        </Box> */}

        <MinhasRespostas
          questions={questions}
          responses={responses}
          currentQuestion={currentQuestion}
          onStepClick={onStepClick}
        />

        <Box sx={{ mt: 2 }}>
          <ProgressBar
            answeredCount={answeredCount}
            totalQuestions={totalQuestions}
            progressPercentage={progressPercentage}
          />
        </Box>
      </Paper>
    </Box>
  );
};

// import { Box, Paper, Typography } from '@mui/material';
// import * as React from 'react';
// import { useLocation } from 'react-router-dom';
// import { getFuncao } from '../../../events/components/EventList/EventMap';
// import { Question } from '../../types/questionnaire.types';
// import { isQuestionAnswered } from '../../utils/questionValidation';
// import { ProgressBar } from '../shared/ProgressBar';

// const getAnswerLetter = (
//   question: Question,
//   responseValue: string | undefined
// ): string => {
//   if (!responseValue) return '–';

//   if (question.type === 'multiple_choice' || question.type === 'true_false') {
//     const idx =
//       question.answers?.findIndex((a) => a.id === responseValue) ?? -1;
//     return idx >= 0 ? String.fromCharCode(65 + idx) : '–';
//   }

//   if (question.type === 'essay') {
//     return responseValue && responseValue !== '-' ? '✓' : '–';
//   }

//   return responseValue ? '✓' : '–';
// };

// interface MinhasRespostasProps {
//   questions: Question[];
//   responses: Record<string, string>;
//   currentQuestion?: number;
//   onStepClick?: (index: number) => void;
// }

// export const MinhasRespostas: React.FC<MinhasRespostasProps> = ({
//   questions,
//   responses,
//   currentQuestion,
//   onStepClick,
// }) => {
//   if (!questions || questions.length === 0) return null;

//   return (
//     <Box sx={{ mt: 2 }}>
//       <Typography
//         variant="caption"
//         sx={{
//           display: 'block',
//           mb: 1.5,
//           fontWeight: 600,
//           color: 'primary.dark',
//           fontSize: '11pt',
//           letterSpacing: '0.5px',
//           textTransform: 'uppercase',
//           textAlign: 'center',
//         }}
//       >
//         Minhas Respostas
//       </Typography>

//       <Box
//         sx={{
//           display: 'flex',
//           flexWrap: 'wrap',
//           justifyContent: 'center',
//           gap: '4px',
//         }}
//       >
//         {questions.map((q, idx) => {
//           const letter = getAnswerLetter(q, responses[q.id]);
//           const isAnswered = letter !== '–';
//           const isCurrent = idx === currentQuestion;
//           return (
//             <Box
//               key={q.id}
//               onClick={() => onStepClick?.(idx)}
//               sx={{
//                 display: 'flex',
//                 flexDirection: 'column',
//                 alignItems: 'center',
//                 cursor: onStepClick ? 'pointer' : 'default',
//                 transform: isCurrent ? 'scale(1.15)' : 'scale(1)',
//                 transition: 'transform 0.2s ease',
//                 '&:hover': onStepClick
//                   ? { transform: 'scale(1.2)' }
//                   : undefined,
//               }}
//             >
//               {/* Número */}
//               <Box
//                 sx={{
//                   width: 24,
//                   height: 20,
//                   display: 'flex',
//                   alignItems: 'center',
//                   justifyContent: 'center',
//                   borderRadius: '4px 4px 0 0',
//                   backgroundColor: isCurrent
//                     ? '#EEEDFE' // índigo-50
//                     : 'rgba(0,0,0,0.04)',
//                   // boxShadow: isCurrent
//                   //   ? '0 0 0 2px #7F77DD' // índigo-400
//                   //   : 'none',
//                   border: 'none',
//                   borderBottom: isCurrent ? '2px solid #7F77DD' : undefined,
//                   borderLeft: isCurrent ? '2px solid #7F77DD' : undefined,
//                   borderRight: isCurrent ? '2px solid #7F77DD' : undefined,
//                   borderTop: isCurrent ? '2px solid #7F77DD' : undefined,
//                   boxShadow: 'none',
//                 }}
//               >
//                 <Typography
//                   sx={{
//                     fontSize: '9pt',
//                     fontWeight: isCurrent ? 700 : 500,
//                     color: isCurrent ? '#3C3489' : '#888780', // índigo-800 ou cinza-400
//                     lineHeight: 1,
//                   }}
//                 >
//                   {idx + 1}
//                 </Typography>
//               </Box>

//               {/* Letra */}
//               <Box
//                 sx={{
//                   width: 24,
//                   height: 22,
//                   display: 'flex',
//                   alignItems: 'center',
//                   justifyContent: 'center',
//                   borderRadius: '0 0 4px 4px',
//                   backgroundColor: isAnswered
//                     ? isCurrent
//                       ? '#CECBF6'
//                       : '#FAC775' // índigo-100 se atual, âmbar-100 se só respondido
//                     : 'rgba(0,0,0,0.04)',
//                   // border: '1px solid',
//                   borderColor: isAnswered
//                     ? isCurrent
//                       ? '#7F77DD'
//                       : '#EF9F27' // índigo-400 ou âmbar-200
//                     : 'rgba(0,0,0,0.08)',
//                   transition: 'all 0.2s ease',
//                   // boxShadow: isCurrent ? '0 0 0 2px #7F77DD' : 'none',
//                   borderLeft: isCurrent
//                     ? '2px solid #7F77DD'
//                     : '1px solid rgba(0,0,0,0.08)',
//                   borderRight: isCurrent
//                     ? '2px solid #7F77DD'
//                     : '1px solid rgba(0,0,0,0.08)',
//                   borderBottom: isCurrent
//                     ? '2px solid #7F77DD'
//                     : '1px solid rgba(0,0,0,0.08)',
//                   borderTop: 'none',
//                   boxShadow: 'none',
//                 }}
//               >
//                 <Typography
//                   sx={{
//                     fontSize: '10pt',
//                     fontWeight: isAnswered ? 700 : 400,
//                     color: isAnswered
//                       ? isCurrent
//                         ? '#26215C'
//                         : '#412402' // índigo-900 ou âmbar-900
//                       : 'text.disabled',
//                     lineHeight: 1,
//                   }}
//                 >
//                   {letter}
//                 </Typography>
//               </Box>
//             </Box>
//           );
//         })}
//       </Box>
//     </Box>
//   );
// };

// interface DadosServidorHeaderProps {
//   questions: Question[];
//   responses: Record<string, string>;
//   currentQuestion?: number;
//   onStepClick?: (index: number) => void;
// }

// export const DadosServidorHeader: React.FC<DadosServidorHeaderProps> = ({
//   questions,
//   responses,
//   currentQuestion = 0,
//   onStepClick,
// }) => {
//   const answeredCount = React.useMemo(
//     () =>
//       questions.filter((q) => isQuestionAnswered(q, responses[q.id])).length,
//     [questions, responses]
//   );
//   const totalQuestions = questions.length;
//   const progressPercentage =
//     totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
//   const location = useLocation();

//   const evtIdMatch = location.pathname.match(/evtid=([^/]+)/i);
//   const evtId = evtIdMatch ? evtIdMatch[1] : '';

//   const nome = localStorage.getItem('name') || '';

//   const nomeDaProva = getFuncao(evtId);

//   if (!evtId) {
//     return null;
//   }

//   return (
//     <Box sx={{ mt: 3, px: { xs: 2, md: 3 } }}>
//       <Paper
//         elevation={0}
//         sx={{
//           maxWidth: 1400,
//           width: '100%', // ← adicionado
//           mx: 'auto',
//           p: { xs: 2.5, md: 3 },
//           borderRadius: 2,
//           backgroundColor: 'rgba(25, 118, 210, 0.08)',
//           border: '1px solid',
//           borderColor: 'rgba(25, 118, 210, 0.3)',
//           position: 'relative',
//           overflow: 'visible', // ← era 'hidden', esse era o culpado
//           boxSizing: 'border-box', // ← adicionado
//           paddingLeft: '20px !important',
//           '&::before': {
//             content: '""',
//             position: 'absolute',
//             left: 0,
//             top: 0,
//             bottom: 0,
//             width: '4px',
//             backgroundColor: 'primary.main',
//           },
//         }}
//       >
//         <Box>
//           <Typography
//             variant="body1"
//             sx={{
//               color: 'text.primary',
//               fontSize: { xs: '0.9rem', md: '1rem' },
//               lineHeight: 1.8,
//               textAlign: 'center',
//               '& .destaque': {
//                 fontWeight: 500,
//                 color: 'primary.dark',
//               },
//             }}
//           >
//             Olá, Professor{' '}
//             <span className="destaque">{nome.toUpperCase()}</span>! Você está
//             realizando a prova referente ao(s) programa(s):{' '}
//             <span className="destaque">{nomeDaProva}</span>.
//           </Typography>
//         </Box>
//         <MinhasRespostas
//           questions={questions}
//           responses={responses}
//           currentQuestion={currentQuestion}
//           onStepClick={onStepClick}
//         />

//         <Box sx={{ mt: 2 }}>
//           <ProgressBar
//             answeredCount={answeredCount}
//             totalQuestions={totalQuestions}
//             progressPercentage={progressPercentage}
//           />
//         </Box>
//       </Paper>
//     </Box>
//   );
// };
